import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import { connect } from "react-redux";
import Stack from "@mui/material/Stack";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { Radio, Paper } from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import DPGroupedBarLineChart from "../DPGroupedBarLineChart";
import BarChart from "../BarChart";
import DPBarMultiple from "../DPBarMultiple";
import GraphService from "../../../../services/console_services/Graph";
import Loading from "../../Loading";
import { setModuleTabIndex } from "../../../../store/Actions";
import BarChartIOF from "./BarChartIOF";
import SingleBarChartIOF from "./SingleBarChartIOF";
import { active } from "d3";
import BarLineChartIOF from "./BarLineChartIOF";
import CustomScrollButton from "../../ReusableComponents/CustomTabScrollButton";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={1}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const useStyles = makeStyles((_theme) => ({
  root: {
    flexGrow: 1,
    dispatch: "flex",
    padding: "10px",
  },
  paper: {
    boxShadow: "none !important",
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "58vh",
    width: "100%",
    position: "relative",
    top: 0,
    left: 0,
  },
  select: {
    borderColor: "transparent",
    borderStyle: "none",
    height: "30px",
    width: "120px",
    borderRadius: "4px !important",
    background: "var(--Neutral-Colors-White, #FFF)",
    fontSize: "14px !important",
    color: "#404041 !important",
    "&.MuiOutlinedInput-root": {
      margin: "0px 17px",
    },
  },
  main: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    padding: "0px 10px",
  },
  appBar: {
    padding: "10px",
  },
  tab: {
    background: "#FFF",
    boxShadow:
      "0px 2px 4px 0px rgba(0, 0, 0, 0.10), 0px 4px 12px 0px rgba(0, 0, 0, 0.10)",
    flex: 1,
    minHeight: "30px",
    borderRadius: "8px",
    // marginRight: "100px",
  },
  activeTabs: {
    color: "#FFF",
    textAlign: "center",
    fontFamily: "Poppins, sans-serif",
    fontSize: "13px",
    fontWeight: "500",
    textTransform: "none",
    borderRight: "1px solid #92949747",
    minHeight: "30px",
    border: "1px solid var(--Neutral-Colors-Off-White, #E6E7E8)",
    background: "var(--Nestle_Blue, #0067A1)",
  },
  inActiveTabs: {
    color: "#59595A",
    fontFamily: "Poppins, sans-serif",
    fontSize: "13px",
    textTransform: "none",
    minHeight: "30px",
    opacity: 1,
    border: "1px solid var(--Neutral-Colors-Off-White, #E6E7E8)",
    background: "var(--Neutral-Colors-White, #FFF)",
  },
  indicator: {
    display: "none",
  },
  filter: {
    fontSize: "14px",
    margin: "0px 2px",
  },
}));

const networkOptions = [
  { entity: "Network", value: "AMBIENT" },
  { entity: "Network", value: "CHILLED" },
  { entity: "Network", value: "FROZEN" },
  { entity: "Network", value: "NPW" },
];

const GraphView_IOF = ({
  tabIndex,
  Module,
  Network,
  DC,
  Products,
  Division,
  PL3,
  graphViewTabList,
  date,
  applyFilter,
  role,
  moduleTabIndexChange,
  CL6,
  Customers,
}) => {
  const classes = useStyles();
  const allData = useSelector((state) => state);
  const { instance } = useMsal();
  const [topOrBottomValue, setTopOrBottomValue] = useState("Top");
  const [countValue, setCountValue] = useState("5");
  const tabValue = useSelector((state) => state?.tabs?.moduleTabIndex);
  const [graphData, setGraphData] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeTab = graphViewTabList.filter(
    (item) => item.index === tabValue
  )[0].name;
  const countList = [1, 2, 3, 4, 5, 10, 20, 30, 40, 50];
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // const [duration, setDuration] = useState(4);
  const [value, setValue] = React.useState([networkOptions[0]]);
  const entireState = useSelector((state) => state);
  const getGraphData = (params) => {
    console.log("getGraphData called");
    setLoading(true);
    GraphService.getGraph(params, instance)
      .then((data) => {
        if (data?.data) {
          setGraphData([...data?.data]);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const memoizedParams = useMemo(() => {
    let params = {
      section: Module,
      type: graphViewTabList[tabValue]?.value,
      arrangements: topOrBottomValue,
      value: countValue,
      filter: {
        RecommendedDC: DC,
        Network,
        PL3,
        Division,
        MaterialID: Products,
        CL6,
        SoldToName: Customers,
        startDate: date[0],
        endDate: date[1],
      },
    };
    return params;
  }, [
    Module,
    tabValue,
    topOrBottomValue,
    countValue,
    role,
    DC,
    Network,
    PL3,
    Division,
    Products,
    date,
    startDate,
    endDate,
    // duration,
    value,
    entireState?.filter?.scenarioValue,
  ]);

  useEffect(() => {
    // if (!memoizedParams.section || !memoizedParams.type || !memoizedParams.name) {
    //   return;
    // }
    getGraphData(memoizedParams);
  }, [memoizedParams, applyFilter]);

  useEffect(() => {
    if (allData.filter.Network?.length > 0) {
      let data = [];

      allData?.filter.Network?.forEach((item) => {
        data.push({ entity: "Network", value: item });
      });
      setValue([...data]);
    }
  }, []);
  useEffect(() => {
    const today = dayjs(); // Get today's date as a Dayjs object
    setStartDate(today);

    const nextMonth = today.add(1, "month"); // Add one month to today's date
    setEndDate(nextMonth);
  }, []);

  const handleChange = (_event, newValue) => {
    moduleTabIndexChange(newValue);
    setTopOrBottomValue("Top");
    setCountValue("5");
  };

  const handleTopBottomChange = (event) => {
    setTopOrBottomValue(event.target.value);
  };

  const handleCountChange = (event) => {
    setCountValue(event.target.value);
  };

  const renderChart = () => {
    if (activeTab === "Utilization") {
      return <SingleBarChartIOF graphData={graphData} activeTab={activeTab} />;
    } else if (activeTab === "Special Orders") {
      return <BarLineChartIOF graphData={graphData} activeTab={activeTab} />;
    } else {
      return (
        <BarChartIOF
          graphData={graphData}
          activeTab={activeTab}
          yAxisTitle={
            activeTab === "Prioritization" ? "Avg Weightage" : "Margin %"
          }
          countValue={countValue}
        />
      );
    }
  };

  return (
    <Paper elevation={0} className={classes.paper}>
      {loading ? (
        <div className={classes.loader}>
          <Loading />
        </div>
      ) : (
        <>
          {/* <div className={classes.main}>
            <div className={classes.appBar}> */}
          <Box
            sx={{
              maxWidth: "70vw",
              overflow: "hidden",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleChange}
              className={classes.tab}
              aria-label="simple tabs example"
              variant="scrollable"
              scrollButtons="auto"
              ScrollButtonComponent={CustomScrollButton}
              classes={{ indicator: classes.indicator }}
            >
              {graphViewTabList.map((item, index) => (
                <Tab
                  key={index}
                  className={
                    tabValue === index
                      ? classes.activeTabs
                      : classes.inActiveTabs
                  }
                  label={item.name}
                  style={{
                    minHeight: "35px",
                  }}
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          </Box>
          {/* </div>
          </div> */}
          {graphViewTabList?.map((_item, index) => (
            <TabPanel key={index} value={tabIndex} index={index}>
              <Stack
                direction="row"
                alignItems="center"
                sx={{ padding: "22px 8px" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div style={{ display: "flex" }}>
                    <>
                      <Select
                        value={topOrBottomValue}
                        onChange={handleTopBottomChange}
                        className={classes.select}
                        displayEmpty
                        inputProps={{ "aria-label": "Without label" }}
                        sx={classes.select}
                      >
                        <MenuItem value="Top">Top</MenuItem>
                        <MenuItem value={"Bottom"}>Bottom</MenuItem>
                      </Select>
                      <Select
                        value={countValue}
                        onChange={handleCountChange}
                        className={classes.select}
                        displayEmpty
                        inputProps={{ "aria-label": "Without label" }}
                        sx={classes.select}
                      >
                        {countList.map((item, _index) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  </div>
                </div>
              </Stack>
              {renderChart()}
            </TabPanel>
          ))}
        </>
      )}
    </Paper>
  );
};

GraphView_IOF.propTypes = {
  tabIndex: PropTypes.number.isRequired,
  tabChange: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  tabIndex: state.tabs.index,
  Network: state.filter.Network,
  DC: state.filter.DC,
  Products: state.filter.Products,
  PL3: state.filter.PL3,
  date: state.filter.Date,
  Division: state.filter.Division,
  applyFilter: state.filter.applyFilter,
  role: state.login.role,
  CL6: state.filter.CL6,
  Customers: state.filter.Customers,
});

const mapDispatchToProps = (dispatch) => ({
  tabChange: (newValue) => dispatch({ type: "TAB_INDEX", payload: newValue }),
  moduleTabIndexChange: (newValue) =>
    dispatch({ type: "MODULE_TAB_INDEX", payload: newValue }),
});

export default connect(mapStateToProps, mapDispatchToProps)(GraphView_IOF);
